import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { format, isAfter } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
  ResponsiveContainer,
} from "recharts";

import Typography from "../../../../components/Typography";

import { ReactComponent as EmptyState } from "../../../../build/images/emptystatemanagerhorizontal.svg";

import "./nps-date-five.css";
import Paperplane from "../../../Loading/Paperplane";
import { getCsatDayCall, getCsatDayChannelCall } from "../../../../actions";
import { groupBy } from "../../../../utils/groupBy";
import axios from "axios";

export default function NpsDateFive() {
  const agentDashboardFilter = useSelector(
    (state) => state.agentDashboardFilter
  );
  const cardsOfCallCenterDash = useSelector(
    (state) => state.cardsOfCallCenterDash
  );

  const [csatDay, setCsatDay] = useState([]);
  const [csatChannel, setCsatChannel] = useState([]);
  const [loadingCsat, setLoadingCsat] = useState(false);
  const [loadingCsatChannel, setLoadingCsatChannel] = useState(false);
  const [midias, setMidias] = useState([]);

  async function getCsatDay(source) {
    setLoadingCsat(true);
    const response = await getCsatDayCall(
      agentDashboardFilter.channelSelectedAgentScreen
        .map((option) => option.id)
        .join(","),
      agentDashboardFilter.avaliationSelectedAgentScreen
        .map((option) => option.id)
        .join(","),
      source
    );

    if (response && response.status === 200) {
      const data = response.data;

      if (data.length) {
        const graphicData = data.map((datum) => {
          return {
            name: `${format(
              new Date(datum.data.replaceAll("-", "/")),
              "dd/MM"
            )}`,
            resultado: Number(datum.resultado),
          };
        });

        setCsatDay(graphicData);
      } else {
        setCsatDay([]);
      }
      setLoadingCsat(false);
    } else {
      setCsatDay([]);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setLoadingCsat(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }
  }

  async function getCsatDayChannel(source) {
    setLoadingCsatChannel(true);
    let updatedMidias = [];
    let updatedCsatChannel = [];
    const response = await getCsatDayChannelCall(
      format(
        new Date(agentDashboardFilter.initialDateAgenteScreen),
        "yyyy-MM-dd"
      ),
      format(new Date(agentDashboardFilter.finalDateAgentScreen), "yyyy-MM-dd"),
      agentDashboardFilter.channelSelectedAgentScreen
        .map((option) => option.id)
        .join(","),
      agentDashboardFilter.avaliationSelectedAgentScreen
        .map((option) => option.id)
        .join(","),
      source
    );

    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const data = response.data;
      const hasData = !!data && data.length > 0;
      if (hasData) {
        data.forEach((datum) => {
          const hasMidia = !!updatedMidias;
          if (hasMidia) {
            const hasThisMidia = !!updatedMidias.find((updatedMidia) => {
              return updatedMidia.id === datum.idmidia;
            });
            if (!hasThisMidia) {
              updatedMidias.push({
                id: datum.idmidia,
                name: datum.desc_midia,
                describer: datum.desc_midia,
              });
            }
          }
        });

        const groupedDataByDate = groupBy(data, "data");

        for (let date in groupedDataByDate) {
          const dataOfThisDate = groupedDataByDate[date];
          const hasDataOfThisDate =
            !!dataOfThisDate && dataOfThisDate.length > 0;

          const objectCsatChannel = {};
          const hasUpdatedMidias = !!updatedMidias;
          if (hasUpdatedMidias) {
            updatedMidias.forEach((updatedMidia) => {
              objectCsatChannel[updatedMidia.name] = 0;
            });
          }
          objectCsatChannel.name = format(new Date(date), "dd/MM");
          if (hasDataOfThisDate) {
            dataOfThisDate.forEach((dataInfo) => {
              objectCsatChannel[dataInfo.desc_midia] = Number(
                dataInfo.resultado
              );
            });
          }
          updatedCsatChannel.push(objectCsatChannel);
        }
      }

      setMidias(updatedMidias);
      const hasUpdatedCsatChannel =
        !!updatedCsatChannel && updatedCsatChannel.length > 0;
      if (hasUpdatedCsatChannel) {
        updatedCsatChannel.sort((a, b) => {
          const [dayA, monthA] = a.name.split("/").map(Number);
          const [dayB, monthB] = b.name.split("/").map(Number);
          const dateA = new Date(2023, monthA - 1, dayA);
          const dateB = new Date(2023, monthB - 1, dayB);

          const isBAfterA = isAfter(dateB, dateA);
          if (isBAfterA) {
            return -1;
          } else {
            return 1;
          }
        });
      }
      setCsatChannel(updatedCsatChannel);
      setLoadingCsatChannel(false);
    } else {
      setCsatChannel(updatedCsatChannel);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setLoadingCsatChannel(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }
  }

  const CustomYAxisTick = ({ x, y, payload }) => {
    return (
      <g transform={`translate(${20},${y + 4})`}>
        <text
          x={0}
          y={0}
          textAnchor="start"
          fill="rgba(0, 0, 0, 0.38)"
          style={{ fontFamily: "Inter", fontSize: 12 }}
        >
          {payload.value}%
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{payload[0].payload.name}</p>
          {payload.map((load, index) => {
            return (
              <Typography.H5 weight="400" color="gray-180" key={index}>
                {load.name.substring(0, 1).toUpperCase() +
                  load.name.substring(1)}
                : {load.value}%
              </Typography.H5>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const handleColors = (id) => {
    if (id === 1) {
      //VOZ
      return "#455cc7";
    } else if (id === 2 || id === 7) {
      //WEBCHAT
      return "#ff7661";
    } else if (id === 3) {
      //EMAIL
      return "#ffbb00";
    } else if (id === 4) {
      //FACEBOOK
      return "#1877F2";
    } else if (id === 5) {
      //WHATSAPP
      return "#008908";
    } else if (id === 8) {
      //TELEGRAM
      return "#29a9ea";
    } else if (id === 11) {
      //GBM
      return "#ea4335";
    } else if (id === 12) {
      //SMS
      return "#61c4be";
    } else {
      return "#455CC7";
    }
  };

  const getAllData = async (sourceDay, sourceChannel) => {
    await getCsatDay(sourceDay);
    await getCsatDayChannel(sourceChannel);
  };

  useEffect(() => {
    const sourceDay = axios.CancelToken.source();
    const sourceChannel = axios.CancelToken.source();
    if (!!agentDashboardFilter) {
      getAllData(sourceDay, sourceChannel);
    }

    return () => {
      sourceDay.cancel("Operation canceled by the user. (sourceDay)");
      sourceChannel.cancel("Operation canceled by the user. (sourceChannel)");
    };
  }, [agentDashboardFilter]);

  return (
    <section className="nps-date-five">
      {cardsOfCallCenterDash.card11 && (
        <div className="nps-date-five__csat-month-call-center">
          <header>
            <h5>CSAT deste mês</h5>
          </header>

          {loadingCsat ? (
            <Paperplane className="nps-date-five__dots-loading" />
          ) : (
            <div className="nps-date-five__csat-month__graphic">
              {csatDay.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={csatDay}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0.39%"
                          stopColor="rgba(69, 92, 199, 0.83)"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="100%"
                          stopColor="rgba(255, 255, 255, 0.00)"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" />
                    <YAxis tick={<CustomYAxisTick />} />
                    <CartesianGrid stroke="#f5f5f5" />
                    <Tooltip />
                    <Area
                      type="linear"
                      dataKey="resultado"
                      stroke="#455CC7"
                      fillOpacity={1}
                      fill="url(#colorUv)"
                      dot={{
                        stroke: "#455CC7",
                        strokeWidth: 2,
                        r: 5,
                        color: "white",
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="card-table__area-empty">
                  <EmptyState />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {cardsOfCallCenterDash.card12 && (
        <div className="nps-date-five__csat-by-channel-call-center">
          <div className="nps-date-five__csat-by-channel-call-center__header">
            <h5>CSAT por canal</h5>

            <div className="nps-date-five__csat-by-channel__tag">
              {midias.map((midia, index) => {
                return (
                  <span
                    className="nps-date-five__csat-by-channel__tag__span"
                    key={index}
                  >
                    <div
                      className={`by-channel-${midia.name.toLowerCase()}__color`}
                    />

                    <h6 className="by-channel-h5">{midia.name}</h6>
                  </span>
                );
              })}
            </div>
          </div>

          {loadingCsatChannel ? (
            <Paperplane className="nps-date-five__dots-loading" />
          ) : (
            <div className="nps-date-five__csat-by-channel__graphic">
              {csatChannel.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={csatChannel}
                    margin={{
                      top: 5,
                      right: 30,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis dataKey="name" />
                    <YAxis tick={<CustomYAxisTick />} />
                    <Tooltip content={<CustomTooltip />} />
                    {midias.map((midia, index) => {
                      return (
                        <Line
                          key={index}
                          type="linear"
                          dataKey={midia.name}
                          stroke={handleColors(midia.id)}
                          dot={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="card-table__area-empty">
                  <EmptyState />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
